import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

const STATUS_CONFIG = {
  New:              { label: 'New',            color: 'bg-blue-50 text-blue-600 border-blue-200' },
  Open:             { label: 'Open',           color: 'bg-amber-50 text-amber-600 border-amber-200' },
  Answered:         { label: 'Answered',       color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  'Customer-Reply': { label: 'Customer Reply', color: 'bg-violet-50 text-violet-600 border-violet-200' },
  Store:            { label: 'Store',          color: 'bg-teal-50 text-teal-600 border-teal-200' },
  Closed:           { label: 'Closed',         color: 'bg-slate-100 text-slate-400 border-slate-200' },
};

const TYPE_LABELS = {
  gen: 'General', general: 'General',
  indent: 'Indent Based', delivery: 'Delivery',
  item: 'Item Based', quality: 'Quality Based',
};

const Spinner = ({ size = 5 }) => (
  <svg className={`w-${size} h-${size} animate-spin text-blue-400`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

const FeedbackComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets]           = useState([]);
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading]           = useState(true);
  const [userRole, setUserRole]         = useState('');

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail]     = useState(null);
  const [replies, setReplies]               = useState([]);
  const [loadingDetail, setLoadingDetail]   = useState(false);
  const [replyMessage, setReplyMessage]     = useState('');
  const [sendingReply, setSendingReply]     = useState(false);
  const [closingTicket, setClosingTicket]   = useState(false);
  const [confirmClose, setConfirmClose]     = useState(null);
  const [closeRemarks, setCloseRemarks]     = useState('');
  const [toast, setToast]                   = useState(null);
  const [lightbox, setLightbox]             = useState(null); // url string

  useEffect(() => {
    try { const u = JSON.parse(localStorage.getItem('user') || '{}'); setUserRole(u.role || ''); }
    catch { setUserRole(''); }
    fetchTickets();
  }, []);

  useEffect(() => {
    if (location.state?.statusFilter) setStatusFilter(location.state.statusFilter);
  }, [location.state]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE_URL}/feedback/list`, { headers: {  } });
      setTickets(!res.data.error && res.data.data ? res.data.data : []);
    } catch (err) {
      setTickets([]);
    } finally { setLoading(false); }
  };

  const fetchDetail = async (id) => {
    try {
      setLoadingDetail(true);

      const res = await axios.get(`${API_BASE_URL}/feedback/${id}/detail`, { headers: {  } });
      if (!res.data.error && res.data.data) { setTicketDetail(res.data.data); setReplies(res.data.data.replies || []); }
      else { setTicketDetail(null); setReplies([]); }
    } catch { setTicketDetail(null); setReplies([]); }
    finally { setLoadingDetail(false); }
  };

  const openTicket = (ticket) => { setSelectedTicket(ticket); setReplyMessage(''); fetchDetail(ticket.id); };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    try {
      setSendingReply(true);

      const res = await axios.post(`${API_BASE_URL}/feedback/response/${selectedTicket.id}`, { message: replyMessage }, { headers: {  } });
      if (!res.data.error) { setReplyMessage(''); fetchDetail(selectedTicket.id); fetchTickets(); showToast('Response sent'); }
      else showToast(res.data.message || 'Failed to send', 'error');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send', 'error');
    } finally { setSendingReply(false); }
  };

  const closeTicket = (id) => { setCloseRemarks(''); setConfirmClose(id); };

  const confirmCloseTicket = async () => {
    if (!closeRemarks.trim()) return;
    const id = confirmClose;
    setConfirmClose(null);
    try {
      setClosingTicket(true);

      const res = await axios.put(`${API_BASE_URL}/feedback/${id}/close`, { remarks: closeRemarks }, { headers: {  } });
      if (!res.data.error) { fetchTickets(); setSelectedTicket(null); showToast('Feedback closed'); }
      else showToast(res.data.message || 'Failed to close', 'error');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to close', 'error');
    } finally { setClosingTicket(false); }
  };

  const filtered = tickets.filter(t => {
    const label = TYPE_LABELS[t.type] || t.type || '';
    const q = searchTerm.toLowerCase();
    const matchSearch = t.id?.toString().includes(q) || t.disctiprion?.toLowerCase().includes(q) || label.toLowerCase().includes(q);
    return matchSearch && (statusFilter === 'all' || t.status === statusFilter);
  });

  const count = (key) => key === 'all' ? tickets.length : tickets.filter(t => t.status === key).length;

  const knownStatuses = Object.keys(STATUS_CONFIG);
  const dynamicStatuses = [...new Set(tickets.map(t => t.status).filter(s => s && knownStatuses.includes(s)))];
  const sidebarItems = [{ key: 'all', label: 'All Feedback' }, ...dynamicStatuses.map(s => ({ key: s, label: STATUS_CONFIG[s].label }))];

  const currentStatus = ticketDetail?.status || selectedTicket?.status;
  const sc = STATUS_CONFIG[currentStatus] || { label: currentStatus, color: 'bg-slate-100 text-slate-500 border-slate-200' };

  return (
    <div className="flex-1 flex overflow-hidden">

      {/* ── Sidebar ── */}
      <aside className="w-56 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        {(userRole === 'ind-apr' || userRole === 'st-ind-apr') && (
          <div className="p-4">
            <button
              onClick={() => navigate('/feed-back/new')}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              New Feedback
            </button>
          </div>
        )}

        <div className={`px-3 ${(userRole === 'ind-apr' || userRole === 'st-ind-apr') ? 'pt-1' : 'pt-4'} pb-2`}>
          <p className="px-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</p>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
          {sidebarItems.map(item => {
            const active = statusFilter === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setStatusFilter(item.key)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                  active ? 'bg-blue-600 text-white font-semibold' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <span className="truncate">{item.label}</span>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ml-2 flex-shrink-0 ${
                  active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {count(item.key)}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Content ── */}
      <div className="flex-1 flex min-w-0 overflow-hidden">

        {/* Feedback list */}
        <div className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-200 ${selectedTicket ? 'w-80 flex-shrink-0' : 'flex-1'}`}>

          {/* List header */}
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="images/support.png" className="w-7 h-7 flex-shrink-0" alt="" />
              <div className="min-w-0">
                <h1 className="text-sm font-bold text-slate-800 leading-tight">Support</h1>
                <p className="text-xs text-slate-400">{filtered.length} {filtered.length === 1 ? 'item' : 'items'}</p>
              </div>
            </div>
            <div className="relative flex-shrink-0">
              <svg className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent w-36"
              />
            </div>
          </div>

          {/* Column headers — only in full view */}
          {!selectedTicket && (
            <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
              {[['#ID',1],['Date',2],['Type',2],['Description',3],['From',1],['Status',1],['',2]].map(([h, span]) => (
                <div key={h} className={`col-span-${span} text-[10px] font-bold text-slate-400 uppercase tracking-widest`}>{h}</div>
              ))}
            </div>
          )}

          {/* Rows */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
                <Spinner size={6} />
                <span className="text-sm">Loading…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <svg className="w-10 h-10 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-sm text-slate-400">No feedback found</p>
              </div>
            ) : selectedTicket ? (
              // Compact list
              <div className="divide-y divide-slate-100">
                {filtered.map(t => {
                  const s = STATUS_CONFIG[t.status] || { color: 'bg-slate-100 text-slate-400 border-slate-200', label: t.status };
                  const active = selectedTicket?.id === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => openTicket(t)}
                      className={`w-full text-left px-4 py-3.5 transition-colors ${active ? 'bg-blue-50 border-l-[3px] border-blue-500' : 'hover:bg-slate-50 border-l-[3px] border-transparent'}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-xs font-bold text-slate-400">#{t.id}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${s.color} flex-shrink-0`}>
                          {s.label || t.status}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-700 truncate leading-snug">
                        {t.disctiprion || '—'}
                      </p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[11px] text-slate-400">{TYPE_LABELS[t.type] || t.type}</span>
                        <span className="text-[11px] text-slate-400">{t.created}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              // Full table
              <div className="divide-y divide-slate-100">
                {filtered.map(t => {
                  const s = STATUS_CONFIG[t.status] || { color: 'bg-slate-100 text-slate-400 border-slate-200', label: t.status };
                  return (
                    <div
                      key={t.id}
                      className="grid grid-cols-12 gap-2 px-5 py-3.5 hover:bg-slate-50 transition-colors items-center"
                    >
                      <div className="col-span-1 text-xs font-bold text-blue-500">#{t.id}</div>
                      <div className="col-span-2 text-xs text-slate-400">{t.created}</div>
                      <div className="col-span-2 text-xs font-medium text-slate-600">{TYPE_LABELS[t.type] || t.type}</div>
                      <div className="col-span-3 text-sm text-slate-700 truncate">
                        {t.disctiprion ? t.disctiprion.substring(0, 38) + (t.disctiprion.length > 38 ? '…' : '') : '—'}
                      </div>
                      <div className="col-span-1 text-xs text-slate-500 truncate">{t.from || '—'}</div>
                      <div className="col-span-1">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${s.color}`}>
                          {s.label || t.status}
                        </span>
                      </div>
                      <div className="col-span-2 flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => openTicket(t)}
                          className="text-[10px] font-semibold px-2.5 py-1 rounded border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all whitespace-nowrap"
                        >
                          View
                        </button>
                        {(userRole === 'ind-apr' || userRole === 'st-ind-apr') && t.status !== 'Closed' && (
                          <button
                            onClick={() => closeTicket(t.id)}
                            className="text-[10px] font-semibold px-2.5 py-1 rounded border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-all whitespace-nowrap"
                          >
                            Close
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Detail panel ── */}
        {selectedTicket && (
          <div className="flex-1 flex flex-col bg-white min-w-0 overflow-hidden">

            {/* Detail header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-slate-800">Feedback #{selectedTicket.id}</h2>
                  <p className="text-xs text-slate-400">{selectedTicket.created}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${sc.color}`}>
                  {sc.label}
                </span>
                {(userRole === 'ind-apr' || userRole === 'st-ind-apr') && currentStatus !== 'Closed' && (
                  <button
                    onClick={() => closeTicket(selectedTicket.id)}
                    disabled={closingTicket}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg border border-red-200 transition-colors disabled:opacity-50"
                  >
                    {closingTicket ? <Spinner size={3} /> : null}
                    {closingTicket ? 'Closing…' : 'Close'}
                  </button>
                )}
              </div>
            </div>

            {/* Meta strip */}
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-x-8 gap-y-2 flex-shrink-0">
              {[
                { label: 'Type',    value: TYPE_LABELS[selectedTicket.type] || selectedTicket.type },
                { label: 'Created', value: selectedTicket.created },
                { label: 'Branch',  value: ticketDetail?.from?.branch || selectedTicket.from?.branch || '—' },
                { label: 'Contact', value: ticketDetail?.from?.contact || selectedTicket.from?.contact || '—' },
              ].map(m => (
                <div key={m.label}>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">{m.label}</p>
                  <p className="text-sm font-semibold text-slate-700">{m.value}</p>
                </div>
              ))}
            </div>

            {/* Description */}
            <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Description</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                {ticketDetail?.remarks || selectedTicket.disctiprion || '—'}
              </p>
            </div>

            {/* Attachments */}
            {ticketDetail?.image_paths?.length > 0 && (
              <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {ticketDetail.image_paths.map((path, i) => {
                    const url = `https://rcs-dms.onlinetn.com/public/${path}`;
                    const isPdf = path.toLowerCase().endsWith('.pdf');
                    return isPdf ? (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                        className="w-16 h-16 rounded-lg border-2 border-slate-200 bg-red-50 flex flex-col items-center justify-center gap-1 hover:border-blue-400 transition-colors cursor-pointer">
                        <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5z"/>
                        </svg>
                        <span className="text-[10px] font-bold text-red-500">PDF</span>
                      </a>
                    ) : (
                      <button key={i} onClick={() => setLightbox(url)}
                        className="w-16 h-16 rounded-lg border-2 border-slate-200 overflow-hidden hover:border-blue-400 transition-colors">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Activity */}
            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
                Activity {replies.length > 0 && `(${replies.length})`}
              </p>

              {loadingDetail ? (
                <div className="flex justify-center pt-8"><Spinner /></div>
              ) : replies.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <svg className="w-8 h-8 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-xs text-slate-400">No responses yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {replies.map((reply, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-blue-600">{(reply.by || 'U').charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl rounded-tl-none px-4 py-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-slate-700">{reply.by}</span>
                          <span className="text-xs text-slate-400">{reply.at}</span>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">{reply.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {/* Reply box — inside scroll area so it flows after content */}
            {userRole === 'rcs-store' && currentStatus !== 'Closed' && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <textarea
                  value={replyMessage}
                  onChange={e => setReplyMessage(e.target.value)}
                  placeholder="Write a response…"
                  rows="3"
                  disabled={sendingReply}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none transition disabled:opacity-60"
                />
                <div className="flex justify-end mt-2.5 pb-2">
                  <button
                    onClick={sendReply}
                    disabled={!replyMessage.trim() || sendingReply}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                  >
                    {sendingReply ? <Spinner size={4} /> : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                    {sendingReply ? 'Sending…' : 'Send Response'}
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm close modal ── */}
      {confirmClose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 animate-slide-up overflow-hidden">
            <div className="px-6 pt-6 pb-5 text-center">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-1">Close this feedback?</h3>
              <p className="text-sm text-slate-500">This cannot be undone. It will be marked as closed.</p>
            </div>
            <div className="px-6 pb-4">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                Closing Remarks <span className="text-red-500">*</span>
              </label>
              <textarea
                value={closeRemarks}
                onChange={e => setCloseRemarks(e.target.value)}
                placeholder="Enter reason for closing…"
                rows="3"
                autoFocus
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none transition"
              />
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setConfirmClose(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmCloseTicket}
                disabled={!closeRemarks.trim()}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Close Feedback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-50 animate-slide-up">
          <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-xl text-white text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'
          }`}>
            {toast.type === 'error'
              ? <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
              : <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg>
            }
            {toast.msg}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-[90vw] max-h-[90vh] rounded-xl shadow-2xl object-contain" onClick={e => e.stopPropagation()} />
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <style>{`
        @keyframes slide-up { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-up { animation: slide-up 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default FeedbackComponent;
